import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SortDescendingOutlined, SortAscendingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation } from 'react-i18next';
import { WhsSlaData, DspSlaData, DetailData } from '../types';
import { LEVEL1_META, LEVEL2_META, getLevel1Label, getLevel2Label, getLevel2LabelInGroup } from '../undeliveredAttribution';
import { useHubDeliveryProgressMonitoringOfSlaQuery } from '../graphql/hooks/useHubDeliveryProgressMonitoringOfSlaQuery';
import { useDeliveryProgressMonitoringOfSlaDetailQuery } from '../graphql/hooks/useDeliveryProgressMonitoringOfSlaQuery';
import {
  DeliveryProgressMonitoringOfSlaPageRequestType,
  DeliveryProgressMonitoringOfSlaDetailPageRequestType,
  DeliveryProgressMonitoringOfSlaFiltersType,
  GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
  GET_BREAK_DRIVER_SIDE_BREAKDOWN,
  type DspDriverBreakdownItemType,
  type NoUpdateBreakType
} from '../graphql/queries/getDeliveryProgressMonitoringOfSlaList';
import {
    SEARCH_DELIVERY_FILTER_AREA_OPTIONS,
    SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS,
    type SearchDeliveryFilterAreaOptionsResponse,
    type SearchDeliveryFilterDriverOptionsResponse,
    type DeliveryFilterOption,
} from '../graphql/queries/searchDeliveryFilterOptions';
import { useQuery, useApolloClient } from '@apollo/client';
import { useWarehouseContext } from '../../../../../contexts/WarehouseContext';
import { useHubWarehouseDspOptions } from '../../../../../hooks/useHubWarehouseDspOptions';
import { SiteType } from '../../../../../graphql/types/SiteType';
import { getDateRangeByPreset, type DateRangePreset } from '../components/SlaMonitoringFilter';
import {
  ARRIVAL_POINT_TYPE_WHS,
  DIMENSION_DSP,
  DIMENSION_WHS,
  DIMENSION_DRIVER,
  parseArrivalPointTypeFromSearch,
  type ArrivalPointType
} from '../constants';
import {
    DriverFilterOption,
    UNASSIGNED_DRIVER_ID,
    buildDriverOptions,
    isUnassignedDriverOnly,
    rememberDriverNames,
    toDriverIds
} from '../../../../../utils/driverFilterOption';
import {
    UndeliveredLevel1,
    UndeliveredLevel2,
    decodeUndeliveredAnomaly,
} from '../undeliveredAttribution';

/** 将仓库 ID 字符串数组转为 number[] 供接口（后端 List<Long>）使用 */
function toWarehouseIdsNum(ids: string[]): number[] {
  return ids.map((id) => parseInt(String(id), 10)).filter((n) => !Number.isNaN(n));
}

/** 详情请求用：未选或结果为 []/[0] 时用 hub 全量列表，仍为空则用 hubId 兜底，避免传 [0] 查不到值 */
function resolveDetailWarehouseIds(
  selected: string[],
  hubWarehouseIds: string[],
  hubId: number | undefined
): number[] {
  let ids = selected.length > 0 ? toWarehouseIdsNum(selected) : toWarehouseIdsNum(hubWarehouseIds);
  if (ids.length === 0 || (ids.length === 1 && ids[0] === 0)) {
    ids = toWarehouseIdsNum(hubWarehouseIds);
  }
  if (ids.length === 0 || (ids.length === 1 && ids[0] === 0)) {
    ids = hubId != null ? [hubId] : [];
  }
  return ids;
}

// 扩展 dayjs 支持时区功能
dayjs.extend(utc);
dayjs.extend(timezone);

export type SlaTableRow = WhsSlaData | DspSlaData;
/** Hub 页筛选器维度：仅 WHS / DSP */
export type SlaMonitoringViewMode = 'whs' | 'dsp';

export interface SlaMonitoringFilters {
    dateRangePreset: DateRangePreset;
    dateRange: [dayjs.Dayjs, dayjs.Dayjs];
    arrivalDate: dayjs.Dayjs;
    selectedCities: string[];
    selectedDsps: string[];
    selectedWarehouses: string[]; // Hub 下可选仓库
    arrivalPointType: ArrivalPointType;
    arrivalTimeDateRange: [dayjs.Dayjs, dayjs.Dayjs] | null; // 限制过去60天
    arrivalTimeDatePreset: 'today' | 'yesterday' | 'last7days' | 'last15days' | 'custom' | null;
    viewMode: SlaMonitoringViewMode;
}

export interface UseSlaMonitoringResult {
    filters: SlaMonitoringFilters;
    setFilters: (filters: SlaMonitoringFilters) => void;
    whsData: WhsSlaData[];
    dspData: DspSlaData[];
    viewMode: SlaMonitoringViewMode;
    handleViewModeChange: (mode: SlaMonitoringViewMode) => void;
    anomalyData: Array<{ type: string; label: string; count: number; color: string }>;
    summaryData: any;
    dsps: Array<{ id: number; name: string }>;
    /** Hub 下子仓库下拉选项（后端结构关联查询返回，与日期无关） */
    warehouses: Array<{ id: number; name: string }>;
    detailFilters: {
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
        selectedStatuses: string[];
        selectedWarehouses: string[]; // Hub WHS 维度下抽屉内选中的仓库 ID 列表
        undeliveredReasonLevel1?: UndeliveredLevel1;
        undeliveredReasonLevel2?: UndeliveredLevel2;
    };
    setDetailFilters: (filters: {
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
        selectedStatuses: string[];
        selectedWarehouses?: string[];
        undeliveredReasonLevel1?: UndeliveredLevel1;
        undeliveredReasonLevel2?: UndeliveredLevel2;
    }) => void;
    areas: Array<{ id: number; name: string }>;
    drivers: DriverFilterOption[];
    /** 详情抽屉用：有详情接口选项时用详情的，否则用主列表选项，避免抽屉内 DSP/区域/司机下拉为空 */
    detailDrawerDsps: Array<{ id: number; name: string }>;
    detailDrawerAreas: Array<{ id: number; name: string }>;
    detailDrawerDrivers: DriverFilterOption[];
    /** 详情抽屉用：DSP-司机级联后的司机下拉选项（选 DSP 时过滤；未选 DSP 全量） */
    drawerDrivers: DriverFilterOption[];
    drawerVisible: boolean;
    drawerTitle: string;
    drawerData: DetailData[];
    drawerColumns: any[];
    setDrawerVisible: (visible: boolean) => void;
    handleDetailClick: (type: string, record: SlaTableRow) => Promise<void>;
    handleAnomalyClick: (anomalyType: string, prefill?: { selectedDsps?: string[]; selectedWarehouses?: string[]; selectedDrivers?: string[] }) => void;
    breakdownDrawerVisible: boolean;
    setBreakdownDrawerVisible: (visible: boolean) => void;
    breakdownData: DspDriverBreakdownItemType[];
    breakdownTitle: string;
    breakdownAnomalyType: string;
    breakdownLoading: boolean;
    breakdownPage: number;
    breakdownPageSize: number;
    breakdownTotal: number;
    breakdownTotalPages: number;
    handleDriverSideBreakdownClick: (anomalyType: string) => Promise<void>;
    handleBreakdownPageChange: (page: number) => void;
    handleBreakdownPageSizeChange: (size: number) => void;
    handleBreakdownRowClick: (anomalyType: string, dspId: number, dspName: string, driverId?: number, driverName?: string, driverIds?: number[]) => void;
    /** 从断更司机侧二级抽屉点进详情时置为 'dsp'，详情抽屉展示 DSP 下拉并预填该行 DSP/司机；关闭抽屉后清空 */
    detailDrawerDimensionOverride: 'dsp' | null;
    loading: boolean;
    error: any;
    pagination: {
        current: number;
        pageSize: number;
        total: number;
    } | null;
    tablePagination?: {
        page: number;
        size: number;
        totalElements: number;
        totalPages: number;
        hasNext: boolean;
        hasPrevious: boolean;
    };
    handlePageChange: (page: number, pageSize?: number) => void;
    handleSortChange: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void;
    handleDetailSortChange: (sortBy: string, sortDirection: 'ASC' | 'DESC') => Promise<void>;
    // 详情分页（受控，后端分页）
    detailPagination: { page: number; size: number; total: number };
    handleDetailPageChange: (page: number, pageSize?: number) => Promise<void>;
    detailSortConfig: { sortBy: string; sortDirection: 'ASC' | 'DESC' };
    detailLoading: boolean;
    orderDetailVisible: boolean;
    orderId: string;
    setOrderDetailVisible: (visible: boolean) => void;
    showOrderDetail: (orderId: number) => void;
    warehouseId: string;
    lastDetailContext: { type: string; record: SlaTableRow; statusField?: string } | null;
    dataUpdatedAtText: string;
    sortConfig: { sortBy: string; sortDirection: 'ASC' | 'DESC' };
}

// 异常预警类型映射
const ANOMALY_TYPE_MAP: { [key: string]: string } = {
    '超24小时未领取': 'isUnpicked24h',
    '即将超时(24小时)': 'isAboutToTimeout24h',
    '即将超时24小时': 'isAboutToTimeout24h',
    '领取超24小时未妥投': 'isUndelivered24h',
    '即将超时(48小时)': 'isAboutToTimeout48h',
    '即将超时48小时': 'isAboutToTimeout48h',
    '领取超48小时未妥投': 'isUndelivered48h',
    '领取超72小时未妥投': 'isUndelivered72h',
    '即将超时(72小时)': 'isAboutToTimeout72h',
    '即将超时72小时': 'isAboutToTimeout72h',
    '失败未返仓': 'isFailedIncomplete',
    '已断更>24h': 'isNoUpdate24h',
    '已断更24h': 'isNoUpdate24h',
    '已断更>72h': 'isNoUpdate72h'
};

// 正常类型映射
const NORMAL_TYPE_MAP: { [key: string]: string } = {
    '实际到件': 'actual_arrivals',
    '已到货': 'actual_arrivals',
    '派送中': 'in_delivery',
    '已妥投': 'delivered_latest',
    '已妥投(最新)': 'delivered_latest',
    '已妥投（最新）': 'delivered_latest',
    '已退件/破损/丢失': 'returned_damaged_lost',
    '失败/未完结': 'failed_incomplete',
    '已领取': 'picked',
    '未领取': 'unpicked',
    '派件失败已返仓': 'failed_returned_to_whs',
    '派件失败未返仓': 'failed_incomplete',
    '退件': 'returned_parcel',
    '丢失/破损': 'lost_damaged',
    '退件开始后72小时内未退件完成': 'return_incomplete_72h',
    '有过返仓扫描且返仓后24小时内无司机领取或退件开始': 'return_warehouse_24h_unprocessed',
    'Incomplete within 72h after return start': 'return_incomplete_72h',
    'Return warehouse scan with no pickup or return start within 24h': 'return_warehouse_24h_unprocessed',
    'Incompleto en 72 h tras inicio de devolución': 'return_incomplete_72h',
    'Escaneo de retorno a almacén sin recogida ni inicio de devolución en 24 h': 'return_warehouse_24h_unprocessed'
};

export const useHubSlaMonitoring = (): UseSlaMonitoringResult => {
    const { t } = useTranslation();
    const location = useLocation();
    const client = useApolloClient();

    const defaultDateRange = getDateRangeByPreset('last3days');
    const today = dayjs().startOf('day');
    // 支持深链 ?arrivalPointType=1 直达「到达HUB时间」视图；不传/非法值回落「到达WHS时间」
    const defaultArrivalPointType = parseArrivalPointTypeFromSearch(location.search);
    const [filters, setFilters] = useState<SlaMonitoringFilters>({
        dateRangePreset: 'last3days',
        dateRange: defaultDateRange,
        arrivalDate: defaultDateRange[1],
        selectedCities: [],
        selectedDsps: [],
        selectedWarehouses: [],
        arrivalPointType: defaultArrivalPointType,
        arrivalTimeDateRange: [today, today],
        arrivalTimeDatePreset: 'today',
        viewMode: DIMENSION_WHS
    });
    
    // 分页状态
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10); // 一页10条
    
    // 排序状态 - 用于保持翻页时的排序配置
    const [sortConfig, setSortConfig] = useState<{
        sortBy: string;
        sortDirection: 'ASC' | 'DESC';
    }>({
        sortBy: 'overallConversionRate', // 默认按整体转化率排序
        sortDirection: 'DESC' // 默认从高到低
    });
    
    // 抽屉相关状态
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [detailDrawerDimensionOverride, setDetailDrawerDimensionOverride] = useState<'dsp' | null>(null);
    const [drawerTitle, setDrawerTitle] = useState('');
    const [drawerData, setDrawerData] = useState<DetailData[]>([]);
    const [drawerColumns, setDrawerColumns] = useState<any[]>([]);

    useEffect(() => {
        if (!drawerVisible) setDetailDrawerDimensionOverride(null);
    }, [drawerVisible]);

    // 详情分页与上下文
    const [detailPage, setDetailPage] = useState(1);
    const [detailPageSize, setDetailPageSize] = useState(10);
    const [detailTotal, setDetailTotal] = useState(0);
    const [lastDetailContext, setLastDetailContext] = useState<{ type: string; record: SlaTableRow; statusField?: string } | null>(null);
    
    // 详情排序状态 - 默认按状态排序（降序，DELIVERY_FAILED排在前面）
    const [detailSortConfig, setDetailSortConfig] = useState<{
        sortBy: string;
        sortDirection: 'ASC' | 'DESC';
    }>({
        sortBy: 'overallStatus',
        sortDirection: 'DESC'
    });
    
    // 订单详情相关状态
    const [orderDetailVisible, setOrderDetailVisible] = useState(false);
    const [orderId, setOrderId] = useState<string>('');

    // 断更司机侧二级页（DSP/司机数量 breakdown）
    const [breakdownDrawerVisible, setBreakdownDrawerVisible] = useState(false);
    const [breakdownData, setBreakdownData] = useState<DspDriverBreakdownItemType[]>([]);
    const [breakdownTitle, setBreakdownTitle] = useState('');
    const [breakdownAnomalyType, setBreakdownAnomalyType] = useState('');
    const [breakdownLoading, setBreakdownLoading] = useState(false);
    const [breakdownPage, setBreakdownPage] = useState(1);
    const [breakdownPageSize, setBreakdownPageSize] = useState(10);
    const [breakdownTotal, setBreakdownTotal] = useState(0);
    const [breakdownTotalPages, setBreakdownTotalPages] = useState(0);
    
    // 详情loading状态
    const [detailLoading, setDetailLoading] = useState(false);
    
    // 详情筛选状态
    const [detailFilters, setDetailFiltersState] = useState<{
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
        selectedStatuses: string[];
        selectedWarehouses: string[];
        undeliveredReasonLevel1?: UndeliveredLevel1;
        undeliveredReasonLevel2?: UndeliveredLevel2;
    }>({
        selectedDsps: [],
        selectedAreas: [],
        selectedDrivers: [],
        selectedStatuses: [],
        selectedWarehouses: []
    });

    const setDetailFilters = useCallback((filters: {
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
        selectedStatuses: string[];
        selectedWarehouses?: string[];
        undeliveredReasonLevel1?: UndeliveredLevel1;
        undeliveredReasonLevel2?: UndeliveredLevel2;
    }) => {
        setDetailFiltersState(prev => ({
            ...prev,
            ...filters,
            selectedWarehouses: filters.selectedWarehouses ?? prev.selectedWarehouses
        }));
    }, []);

    // 详情筛选选项（从详情查询响应中获取）
    const [detailFilterOptions, setDetailFilterOptions] = useState<{
        dsps: Array<{ id: number; name: string }>;
        areas: Array<{ id: number; name: string }>;
        drivers: Array<{ id: number; name: string }>;
    } | null>(null); // null 表示还未从详情查询中获取，非 null 表示已获取（即使为空数组）

    // Hub 页：使用 Hub 分页列表查询（dimension 为 whs/dsp）
    const {
        summaryData,
        recordData,
        loading,
        error,
        handleFilter,
        pagination: apiPagination,
        hubWarehouseIds
    } = useHubDeliveryProgressMonitoringOfSlaQuery();

    const {
        detailData,
        pagination, 
        handlePageRequest 
    } = useDeliveryProgressMonitoringOfSlaDetailQuery('whs');

    const { currentWarehouse } = useWarehouseContext();
    const hubId = currentWarehouse?.siteType === SiteType.HUB && currentWarehouse?.id
        ? Number(currentWarehouse.id)
        : undefined;

    // WHS/DSP 下拉改用后端结构关联查询（hub↔whs、whs↔dsp），与日期无关；选中 WHS 时带 warehouseIds 级联过滤 DSP
    const {
        warehouses: structuralWarehouses,
        dsps: structuralDsps,
        loading: warehousesLoading
    } = useHubWarehouseDspOptions(hubId, filters.selectedWarehouses);

    // 转换 API 数据为 WHS/DSP 维度表格行
    const whsData: WhsSlaData[] = filters.viewMode === DIMENSION_WHS
        ? recordData.map(record => ({
            id: record.id || '',
            warehouseId: Number(record.warehouseId) ?? 0,
            warehouseName: record.warehouseName ?? '-',
            arrivalPointType: record.arrivalPointType ?? 0,
            actualArrivals: record.actualArrivals,
            pickedCount: record.pickedCount || 0,
            unpickedCount: record.unpickedCount || 0,
            inProgress: record.inProgress,
            deliveredLatest: record.deliveredLatest,
            failedIncomplete: record.failedIncomplete,
            latestDeliveryRate: parseFloat(record.latestDeliveryRate ?? '0'),
            failedReturnedToWhsCount: record.failedReturnedToWhsCount || 0,
            returnedParcelCount: record.returnedParcelCount || 0,
            lostDamagedCount: record.lostDamagedCount ?? 0,
            returnedParcelIncompleteOver72hRate: parseFloat(record.returnedParcelIncompleteOver72hRate ?? '0'),
            failedReturnedToWhsUnprocessedOver24hRate: parseFloat(record.failedReturnedToWhsUnprocessedOver24hRate ?? '0'),
            t1DeliveryRate: parseFloat(record.t1DeliveryRate ?? '0'),
            t2DeliveryRate: parseFloat(record.t2DeliveryRate ?? '0'),
            t3DeliveryRate: parseFloat(record.t3DeliveryRate ?? '0'),
            t4DeliveryRate: parseFloat(record.t4DeliveryRate ?? '0'),
            t5DeliveryRate: parseFloat(record.t5DeliveryRate ?? '0'),
            driverSideLcCount: Number(record.driverSideLcCount ?? 0),
            warehouseSideLcCount: Number(record.warehouseSideLcCount ?? 0),
            handoverDiffLcCount: Number(record.handoverDiffLcCount ?? 0),
            returnLcCount: Number(record.returnLcCount ?? 0),
            outForDeliveryNodeLcCount: Number(record.outForDeliveryNodeLcCount ?? 0),
            failedNotReturnedNodeLcCount: Number(record.failedNotReturnedNodeLcCount ?? 0),
            lostDamagedDriverLcCount: Number(record.lostDamagedDriverLcCount ?? 0),
            returnWrongUnscanNodeLcCount: Number(record.returnWrongUnscanNodeLcCount ?? 0),
            notPickedUpLcCount: Number(record.notPickedUpLcCount ?? 0),
            returnNotPutAwayLcCount: Number(record.returnNotPutAwayLcCount ?? 0),
            returnOnPutAwayLcCount: Number(record.returnOnPutAwayLcCount ?? 0),
            returnUnPutAwayLcCount: Number(record.returnUnPutAwayLcCount ?? 0),
            lostDamagedWhsLcCount: Number(record.lostDamagedWhsLcCount ?? 0),
            otherLcCount: Number(record.otherLcCount ?? 0),
            handoverDiffNodeLcCount: Number(record.handoverDiffNodeLcCount ?? 0),
            returnPkgLcCount: Number(record.returnPkgLcCount ?? 0),
            delivered36hCount: Number(record.delivered36hCount ?? 0),
        }))
        : [];

    const dspData: DspSlaData[] = filters.viewMode === DIMENSION_DSP
        ? recordData.map(record => ({
            id: record.id || '',
            dsp: record.dspName ?? `DSP-${record.dspId}`,
            dspId: record.dspId,
            arrivalPointType: record.arrivalPointType ?? 0,
            actualArrivals: record.actualArrivals,
            pickedCount: record.pickedCount || 0,
            unpickedCount: record.unpickedCount || 0,
            inProgress: record.inProgress,
            deliveredLatest: record.deliveredLatest,
            failedIncomplete: record.failedIncomplete,
            latestDeliveryRate: parseFloat(record.latestDeliveryRate ?? '0'),
            failedReturnedToWhsCount: record.failedReturnedToWhsCount || 0,
            returnedParcelCount: record.returnedParcelCount || 0,
            lostDamagedCount: record.lostDamagedCount ?? 0,
            returnedParcelIncompleteOver72hRate: parseFloat(record.returnedParcelIncompleteOver72hRate ?? '0'),
            failedReturnedToWhsUnprocessedOver24hRate: parseFloat(record.failedReturnedToWhsUnprocessedOver24hRate ?? '0'),
            t1DeliveryRate: parseFloat(record.t1DeliveryRate ?? '0'),
            t2DeliveryRate: parseFloat(record.t2DeliveryRate ?? '0'),
            t3DeliveryRate: parseFloat(record.t3DeliveryRate ?? '0'),
            t4DeliveryRate: parseFloat(record.t4DeliveryRate ?? '0'),
            t5DeliveryRate: parseFloat(record.t5DeliveryRate ?? '0'),
            driverSideLcCount: Number(record.driverSideLcCount ?? 0),
            warehouseSideLcCount: Number(record.warehouseSideLcCount ?? 0),
            handoverDiffLcCount: Number(record.handoverDiffLcCount ?? 0),
            returnLcCount: Number(record.returnLcCount ?? 0),
            outForDeliveryNodeLcCount: Number(record.outForDeliveryNodeLcCount ?? 0),
            failedNotReturnedNodeLcCount: Number(record.failedNotReturnedNodeLcCount ?? 0),
            lostDamagedDriverLcCount: Number(record.lostDamagedDriverLcCount ?? 0),
            returnWrongUnscanNodeLcCount: Number(record.returnWrongUnscanNodeLcCount ?? 0),
            notPickedUpLcCount: Number(record.notPickedUpLcCount ?? 0),
            returnNotPutAwayLcCount: Number(record.returnNotPutAwayLcCount ?? 0),
            returnOnPutAwayLcCount: Number(record.returnOnPutAwayLcCount ?? 0),
            returnUnPutAwayLcCount: Number(record.returnUnPutAwayLcCount ?? 0),
            lostDamagedWhsLcCount: Number(record.lostDamagedWhsLcCount ?? 0),
            otherLcCount: Number(record.otherLcCount ?? 0),
            handoverDiffNodeLcCount: Number(record.handoverDiffNodeLcCount ?? 0),
            returnPkgLcCount: Number(record.returnPkgLcCount ?? 0),
            delivered36hCount: Number(record.delivered36hCount ?? 0),
        }))
        : [];

    // 转换异常数据（已移除，不再使用 anomalySummary）
    const anomalyData: Array<{ type: string; label: string; count: number; color: string }> = [];

    // 与 getDeliveryProgressMonitoringOfSlaPageList 的 filter 同结构；首轮无 dsps 时 dspIds 为 undefined
    const filterForOptions = useMemo(() => {
        const hasDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
        const arrivalDateStart = hasDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const arrivalDateEnd = hasDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const warehouseIds: number[] | undefined = filters.selectedWarehouses.length > 0
            ? toWarehouseIdsNum(filters.selectedWarehouses)
            : (hubWarehouseIds.length > 0 ? toWarehouseIdsNum(hubWarehouseIds) : undefined);
        return {
            hubId: hubId ?? undefined,
            arrivalDateStart,
            arrivalDateEnd,
            warehouseIds,
            dspIds: undefined as number[] | undefined,
            areaIds: undefined as number[] | undefined,
            cityNames: undefined as string[] | undefined,
            driverIds: undefined as number[] | undefined,
            arrivalPointType: filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS,
            dimension: filters.viewMode,
        };
    }, [
        hubId,
        filters.arrivalDate,
        filters.arrivalTimeDateRange,
        filters.arrivalPointType,
        filters.viewMode,
        filters.selectedWarehouses,
        hubWarehouseIds,
    ]);

    const listFilterRef = useRef<DeliveryProgressMonitoringOfSlaFiltersType | undefined>(undefined);

    // DSP 下拉来源改为后端结构关联查询（whs↔dsp），与日期无关，随所选 WHS 级联
    const mainListDsps = structuralDsps;

    // 区域/司机改为按关键词查主库（原先由 FilterOptions 聚合 SLA 大表，已不再返回这两类）
    const [areaKeyword, setAreaKeyword] = useState('');
    const [driverKeyword, setDriverKeyword] = useState('');
    const optionScopeWarehouseIds = useMemo(
        () => (filters.selectedWarehouses.length > 0
            ? toWarehouseIdsNum(filters.selectedWarehouses)
            : (hubWarehouseIds.length > 0 ? toWarehouseIdsNum(hubWarehouseIds) : undefined)),
        [filters.selectedWarehouses, hubWarehouseIds]
    );

    const { data: areaOptionsData, loading: areaOptionsLoading } = useQuery<SearchDeliveryFilterAreaOptionsResponse>(
        SEARCH_DELIVERY_FILTER_AREA_OPTIONS,
        {
            variables: { hubId: hubId ?? undefined, warehouseIds: optionScopeWarehouseIds, keyword: areaKeyword || null },
            skip: hubId == null
        }
    );
    const { data: driverOptionsData, loading: driverOptionsLoading } = useQuery<SearchDeliveryFilterDriverOptionsResponse>(
        SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS,
        {
            variables: { hubId: hubId ?? undefined, warehouseIds: optionScopeWarehouseIds, keyword: driverKeyword || null },
            skip: hubId == null
        }
    );

    // 下游用「名称」反查 id 提交筛选，搜索结果随关键词变化，
    // 故累积缓存已见过的 名称→id，避免换词后已选项查不到 id 导致筛选静默失效
    const areaIdByName = useRef<Map<string, number>>(new Map());
    // 司机下拉的 value 是 accountId，反向缓存用于已选项回显
    const driverNameById = useRef<Map<number, string>>(new Map());
    const searchedAreas = areaOptionsData?.searchDeliveryFilterAreaOptions;
    const searchedDrivers = driverOptionsData?.searchDeliveryFilterDriverOptions;
    (searchedAreas ?? []).forEach((option) => areaIdByName.current.set(option.name, option.id));
    rememberDriverNames(searchedDrivers, driverNameById.current);

    /** 搜索结果并入「已选项」，否则 antd Select 无法回显已选标签 */
    const mergeSelectedIntoOptions = (
        searched: DeliveryFilterOption[] | undefined,
        selectedNames: string[] | undefined,
        idByName: Map<string, number>
    ): DeliveryFilterOption[] => {
        const merged = [...(searched ?? [])];
        (selectedNames ?? []).forEach((name) => {
            if (!name || merged.some((option) => option.name === name)) return;
            merged.push({ id: idByName.get(name) ?? -1, name });
        });
        return merged;
    };

    // Hub 页主筛选只有 WHS/DSP，区域与司机下拉只出现在详情抽屉，
    // 故已选项的回显在下面 detailDrawer* / drawerDrivers 处按 detailFilters 合并
    const mainListAreas = useMemo(
        () => mergeSelectedIntoOptions(searchedAreas, detailFilters.selectedAreas, areaIdByName.current),
        [searchedAreas, detailFilters.selectedAreas]
    );
    const mainListDrivers = useMemo(
        () => buildDriverOptions(searchedDrivers, detailFilters.selectedDrivers, driverNameById.current),
        [searchedDrivers, detailFilters.selectedDrivers]
    );
    // 主列表筛选始终用 FilterOptions 接口数据，避免被详情接口的 detailFilterOptions 覆盖导致主列表下拉变空
    const dsps = mainListDsps;
    const areas = mainListAreas;
    const drivers = mainListDrivers;
    // 详情抽屉：区域/司机下拉只展示唯一项，避免同名区域/同一司机在不同 WHS 下重复出现
    const detailDrawerDsps = mainListDsps;
    const detailDrawerAreas = useMemo(
        () => {
            const seen = new Set<string>();
            const result: Array<{ id: number; name: string }> = [];
            mainListAreas.forEach((area: { id: number; name: string }) => {
                const key = `${area.id}|${area.name}`;
                if (!area?.name || seen.has(key)) return;
                seen.add(key);
                result.push(area);
            });
            return result;
        },
        [mainListAreas]
    );
    const detailDrawerDrivers = useMemo(
        () => {
            const seen = new Set<number>();
            return mainListDrivers.filter((driver) => {
                if (seen.has(driver.accountId)) return false;
                seen.add(driver.accountId);
                return true;
            });
        },
        [mainListDrivers]
    );

    // 详情抽屉内 DSP-司机级联：
    // - `detailDrawerDrivers` 永远是全量司机（不随抽屉内筛选变化）
    // - `drawerDrivers` 会在抽屉内选中 DSP 时，通过 options 接口拿到“该 DSP 下的司机子集”
    const drawerCascadedDspIds = useMemo(() => {
        if (!detailFilters.selectedDsps || detailFilters.selectedDsps.length === 0) return null;
        const ids = detailFilters.selectedDsps
            .map((name) => detailDrawerDsps.find((d: { id: number; name: string }) => d.name === name)?.id)
            .filter((id): id is number => id != null);
        return ids.length > 0 ? ids : null;
    }, [detailFilters.selectedDsps, detailDrawerDsps]);

    const drawerCascadedFilterForOptions = useMemo(() => {
        const hasDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
        const arrivalDateStart = hasDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const arrivalDateEnd = hasDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const warehouseIds: number[] | undefined = filters.selectedWarehouses.length > 0
            ? toWarehouseIdsNum(filters.selectedWarehouses)
            : (hubWarehouseIds.length > 0 ? toWarehouseIdsNum(hubWarehouseIds) : undefined);
        // 详情抽屉 DSP→司机级联：固定使用 'driver' 维度，确保后端根据 dspIds 过滤司机选项
        // Hub 页面的 viewMode 是 'whs'/'dsp'，如果直接透传，后端不会按司机维度返回 drivers。
        return {
            hubId: hubId ?? undefined,
            arrivalDateStart,
            arrivalDateEnd,
            warehouseIds,
            dspIds: drawerCascadedDspIds ?? undefined,
            areaIds: undefined as number[] | undefined,
            cityNames: undefined as string[] | undefined,
            driverIds: undefined as number[] | undefined,
            arrivalPointType: filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS,
            dimension: DIMENSION_DRIVER,
        };
    }, [
        hubId,
        drawerCascadedDspIds,
        filters.arrivalDate,
        filters.arrivalTimeDateRange,
        filters.arrivalPointType,
        filters.selectedWarehouses,
        hubWarehouseIds,
    ]);

    // 抽屉内 DSP→司机级联同样走主库按需搜索，与主列表口径一致
    const [drawerDriverKeyword, setDrawerDriverKeyword] = useState('');
    const { data: drawerCascadedDriverData, loading: drawerDriverOptionsLoading } =
        useQuery<SearchDeliveryFilterDriverOptionsResponse>(SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS, {
            variables: {
                hubId: hubId ?? undefined,
                warehouseIds: optionScopeWarehouseIds,
                dspIds: drawerCascadedDspIds ?? undefined,
                keyword: drawerDriverKeyword || null
            },
            skip: hubId == null || !drawerVisible
        });

    const drawerDrivers = useMemo(() => {
        const searched = drawerCascadedDriverData?.searchDeliveryFilterDriverOptions;
        rememberDriverNames(searched, driverNameById.current);
        if (!searched) return detailDrawerDrivers;
        return buildDriverOptions(searched, detailFilters.selectedDrivers, driverNameById.current);
    }, [drawerCascadedDriverData, detailDrawerDrivers, detailFilters.selectedDrivers]);

    // 约束：司机选择必须来自当前下拉选项，否则筛选条件与下拉展示不一致。
    // 因此当 DSP 改变后，如果“已选司机”不在 `drawerDrivers` 中，自动清理无效项；「未分配」哨兵不受 DSP 收窄影响。
    useEffect(() => {
        if (!drawerVisible || !drawerCascadedDspIds) return;
        if (!detailFilters.selectedDrivers || detailFilters.selectedDrivers.length === 0) return;
        const allowed = new Set(drawerDrivers.map((d) => String(d.accountId)));
        const nextSelectedDrivers = detailFilters.selectedDrivers.filter((value) => value === String(UNASSIGNED_DRIVER_ID) || allowed.has(value));
        if (nextSelectedDrivers.length === detailFilters.selectedDrivers.length) return;
        setDetailFilters({
            ...detailFilters,
            selectedDrivers: nextSelectedDrivers,
        });
    }, [drawerVisible, drawerCascadedDspIds, drawerDrivers, detailFilters]);

    // 与 getDeliveryProgressMonitoringOfSlaPageList 的 filter 完全一致（仅无分页），供主列表与后续逻辑使用
    const listFilter = useMemo(() => {
        const hasDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
        const arrivalDateStart = hasDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const arrivalDateEnd = hasDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const selectedDspIdsFromFilter = filters.selectedDsps.length > 0
            ? filters.selectedDsps.map((dspName: string) => {
                const dsp = dsps.find((d: { name: string; id: number }) => d.name === dspName);
                return dsp ? dsp.id : null;
            }).filter((id: number | null) => id !== null) as number[]
            : undefined;
        const finalDspIds = selectedDspIdsFromFilter;
        const warehouseIds: number[] | undefined = filters.selectedWarehouses.length > 0
            ? toWarehouseIdsNum(filters.selectedWarehouses)
            : (hubWarehouseIds.length > 0 ? toWarehouseIdsNum(hubWarehouseIds) : undefined);
        return {
            hubId: hubId ?? undefined,
            arrivalDateStart,
            arrivalDateEnd,
            warehouseIds,
            dspIds: finalDspIds,
            areaIds: undefined as number[] | undefined,
            cityNames: undefined as string[] | undefined,
            driverIds: undefined as number[] | undefined,
            arrivalPointType: filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS,
            dimension: filters.viewMode,
        };
    }, [
        hubId,
        filters.arrivalDate,
        filters.arrivalTimeDateRange,
        filters.arrivalPointType,
        filters.viewMode,
        filters.selectedDsps,
        filters.selectedWarehouses,
        dsps,
        hubWarehouseIds,
    ]);
    listFilterRef.current = listFilter;

    // 获取详情数据（Hub 页 record 为 WhsSlaData | DspSlaData）
    const fetchDetailData = async (
        type: string,
        record: WhsSlaData | DspSlaData,
        sortBy?: string,
        sortDirection?: 'ASC' | 'DESC',
        page?: number,
        size?: number,
        overrideFilters?: {
            selectedDsps?: string[];
            selectedWarehouses?: string[];
            selectedAreas?: string[];
            selectedDrivers?: string[];
            selectedStatuses?: string[];
            undeliveredReasonLevel1?: UndeliveredLevel1;
            undeliveredReasonLevel2?: UndeliveredLevel2;
        }
    ): Promise<{ items: DetailData[]; total: number }> => {
        const currentSelectedStatuses = overrideFilters?.selectedStatuses ?? detailFilters.selectedStatuses;
        // 原因不能用 ??：传了 overrideFilters 即为本次完整的初始筛选，不带原因也要盖掉 state 里上一次残留的值
        const currentUndeliveredReasonLevel1 = overrideFilters ? overrideFilters.undeliveredReasonLevel1 : detailFilters.undeliveredReasonLevel1;
        const currentUndeliveredReasonLevel2 = overrideFilters ? overrideFilters.undeliveredReasonLevel2 : detailFilters.undeliveredReasonLevel2;
        let statusField: string | undefined;
        let statusFields: string[] | undefined;

        if (currentSelectedStatuses.length > 0) {
            if (currentSelectedStatuses.length === 1) {
                statusField = currentSelectedStatuses[0];
            } else {
                statusFields = currentSelectedStatuses;
            }
        }
        // 清空状态时不传 statusField/statusFields，后端不加重状态条件，返回所有状态的数据

        const currentSelectedDsps = overrideFilters?.selectedDsps ?? detailFilters.selectedDsps;
        const currentSelectedWarehouses = overrideFilters?.selectedWarehouses ?? (detailFilters.selectedWarehouses?.length ? detailFilters.selectedWarehouses : filters.selectedWarehouses);

        let selectedDspIds: number[] | undefined;
        if (currentSelectedDsps.length > 0) {
            selectedDspIds = currentSelectedDsps.map(dspName => {
                const dsp = dsps.find((d: any) => d.name === dspName);
                return dsp ? dsp.id : null;
            }).filter(id => id !== null) as number[];
            if (selectedDspIds.length === 0 && record && 'dspId' in record) {
                selectedDspIds = [record.dspId];
            }
        } else if (record && 'dspId' in record) {
            selectedDspIds = [record.dspId];
        }

        // Hub 详情：未选时用 hub 全量，避免 [] 被后端当成 [0] 导致查不到值
        const warehouseIdsRaw = currentSelectedWarehouses.length > 0
            ? currentSelectedWarehouses
            : (record && 'warehouseId' in record ? [String(record.warehouseId)] : hubWarehouseIds);
        const warehouseIds = resolveDetailWarehouseIds(warehouseIdsRaw, hubWarehouseIds, hubId ?? undefined);

        const currentSelectedAreas = overrideFilters?.selectedAreas ?? detailFilters.selectedAreas;
        const currentSelectedDrivers = overrideFilters?.selectedDrivers ?? detailFilters.selectedDrivers;
        // 名称→id 一律走累积缓存：选项数组是按关键词搜索的有限结果，且行下钻当帧还没并入已选项
        const selectedAreaIds = currentSelectedAreas.length > 0
            ? currentSelectedAreas
                .map(areaName => areaIdByName.current.get(areaName) ?? null)
                .filter((id): id is number => id !== null)
            : undefined;
        const isDriverUnassignedOnly = isUnassignedDriverOnly(currentSelectedDrivers);
        const selectedDriverIds = isDriverUnassignedOnly ? undefined : toDriverIds(currentSelectedDrivers);

        const selectedCityNames = filters.selectedCities?.length ? filters.selectedCities : undefined;
        const hasDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
        const detailArrivalDateStart = hasDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const detailArrivalDateEnd = hasDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const pageRequest: DeliveryProgressMonitoringOfSlaDetailPageRequestType = {
            filter: {
                arrivalDateStart: detailArrivalDateStart,
                arrivalDateEnd: detailArrivalDateEnd,
                hubId: hubId ?? undefined,
                warehouseIds,
                dspIds: selectedDspIds,
                areaIds: selectedAreaIds,
                driverIds: selectedDriverIds,
                driverIdNullOnly: isDriverUnassignedOnly ? true : undefined,
                cityNames: selectedCityNames,
                trackingNumber: undefined,
                statusField: statusField,
                statusFields: statusFields,
                arrivalPointType: (filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS),
                undeliveredReasonLevel1: currentUndeliveredReasonLevel1,
                undeliveredReasonLevel2: currentUndeliveredReasonLevel2,
            },
            page: (page ?? detailPage) - 1,
            size: size ?? detailPageSize,
            sortBy: sortBy || detailSortConfig.sortBy,
            sortDirection: sortDirection || detailSortConfig.sortDirection
        };

        try {
            // 直接调用详情查询API
            const { data: detailResponse } = await client.query({
                query: GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
                variables: { pageRequest }
            });

            const detailResp = detailResponse?.getDeliveryProgressMonitoringOfSlaDetailList;

            if (!detailResp) {
                console.error('detailResp 为空');
                return { items: [], total: 0 };
            }

            const detailItems = detailResp?.content || [];
            
            // 转换数据格式
            const convertedData: DetailData[] = detailItems.map((item: any) => ({
                id: item.id,
                dsp_name: (item.lastOperateDspName ?? item.lastAssignDspName) || '',
                warehouse_name: item.warehouseName || '',
                area_name: item.areaName || '',
                driver_id: item.lastOperateDriverId ?? item.lastAssignDriverId ?? 0,
                driver_name: (item.lastOperateDriverName ?? item.lastAssignDriverName) || '-',
                last_operate_dsp_name: item.lastOperateDspName || '',
                last_operate_driver_name: item.lastOperateDriverName || '',
                last_assign_dsp_name: item.lastAssignDspName || '',
                last_assign_driver_name: item.lastAssignDriverName || '',
                task_date: item.taskDate || '',
                tracking_number: item.trackingNumber,
                status: item.overallStatusText || item.overallStatus?.toString() || '',
                undelivered_reason_level_1: item.undeliveredReasonLevel1 || undefined,
                undelivered_reason_level_2: item.undeliveredReasonLevel2 || undefined,
                order_id: item.orderId
            }));
            
            const totalElements: number = detailResp?.totalElements ?? detailItems.length;
            
            return { items: convertedData, total: totalElements };
        } catch (error) {
            console.error('fetchDetailData 获取详情数据失败:', error);
            return { items: [], total: 0 };
        }
    };


    // 根据类型获取列定义
    // 显示订单详情
    const showOrderDetail = (orderId: number) => {
        if (orderId) {
            setOrderId(orderId.toString());
            setOrderDetailVisible(true);
        }
    };

    const getColumnsByType = (type: string, onSortChange?: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void) => {
        // 排序图标渲染函数
        const renderSortIcon = (field: string) => {
            const isActive = detailSortConfig.sortBy === field;
            const isDesc = detailSortConfig.sortDirection === 'DESC';
            
            if (isActive) {
                return isDesc 
                    ? React.createElement(SortDescendingOutlined, { className: "ml-1 cursor-pointer text-blue-500" })
                    : React.createElement(SortAscendingOutlined, { className: "ml-1 cursor-pointer text-blue-500" });
            } else {
                return React.createElement(SortDescendingOutlined, { className: "ml-1 cursor-pointer text-gray-400" });
            }
        };

        const baseColumns = [
            {
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.whsName'),
                dataIndex: 'warehouse_name',
                key: 'warehouse_name',
                render: (text: string) => text ?? '-'
            },
            { 
                title: React.createElement('span', null,
                    t('wms.delivery.parcelSlaMonitoring.detailColumns.area'),
                    onSortChange && React.createElement('span', {
                        onClick: () => {
                            const newDirection = detailSortConfig.sortBy === 'areaId' && detailSortConfig.sortDirection === 'ASC' ? 'DESC' : 'ASC';
                            onSortChange('areaId', newDirection);
                        }
                    }, renderSortIcon('areaId'))
                ), 
                dataIndex: 'area_name', 
                key: 'area_name' 
            },
            { 
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.lastOperateDsp'),
                dataIndex: 'last_operate_dsp_name', 
                key: 'last_operate_dsp_name',
                render: (text: string) => text || '-'
            },
            { 
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.lastOperateDriver'),
                dataIndex: 'last_operate_driver_name', 
                key: 'last_operate_driver_name',
                render: (text: string) => text || '-'
            },
            { 
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.lastAssignDsp'),
                dataIndex: 'last_assign_dsp_name', 
                key: 'last_assign_dsp_name',
                render: (text: string) => text || '-'
            },
            { 
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.lastAssignDriver'),
                dataIndex: 'last_assign_driver_name', 
                key: 'last_assign_driver_name',
                render: (text: string) => text || '-'
            },
            { title: t('wms.delivery.parcelSlaMonitoring.detailColumns.taskDate'), dataIndex: 'task_date', key: 'task_date' },
            { 
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.trackingNumber'), 
                dataIndex: 'tracking_number', 
                key: 'tracking_number',
                render: (text: string, record: any) => React.createElement('a', {
                    href: '#',
                    onClick: (e: React.MouseEvent) => {
                        e.preventDefault();
                        showOrderDetail(record.order_id);
                    },
                    style: { color: '#1890ff' }
                }, text)
            },
            {
                title: React.createElement('span', null,
                    t('wms.delivery.parcelSlaMonitoring.detailColumns.status'),
                    onSortChange && React.createElement('span', {
                        onClick: () => {
                            const newDirection = detailSortConfig.sortBy === 'overallStatus' && detailSortConfig.sortDirection === 'ASC' ? 'DESC' : 'ASC';
                            onSortChange('overallStatus', newDirection);
                        }
                    }, renderSortIcon('overallStatus'))
                ),
                dataIndex: 'status',
                key: 'status'
            },
            {
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.undeliveredReasonLevel1'),
                dataIndex: 'undelivered_reason_level_1',
                key: 'undelivered_reason_level_1',
                render: (value?: string) => {
                    const meta = value ? LEVEL1_META[value as UndeliveredLevel1] : undefined;
                    if (!meta) return '-';
                    return React.createElement('span', {
                        style: {
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 4,
                            color: '#fff',
                            backgroundColor: meta.color,
                            fontSize: 12,
                            whiteSpace: 'nowrap',
                        },
                    }, getLevel1Label(t, value as UndeliveredLevel1));
                },
            },
            {
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.undeliveredReasonLevel2'),
                dataIndex: 'undelivered_reason_level_2',
                key: 'undelivered_reason_level_2',
                render: (value?: string) => {
                    if (!value) return '-';
                    const meta = LEVEL2_META[value as UndeliveredLevel2];
                    return meta ? getLevel2Label(t, value as UndeliveredLevel2) : value;
                },
            }
        ];

        if (type === 'delivered_latest') {
            return [...baseColumns, { title: t('wms.delivery.parcelSlaMonitoring.detailColumns.deliveredTime'), dataIndex: 'delivered_time', key: 'delivered_time' }];
        }

        if (type === 'failed_incomplete') {
            return baseColumns;
        }

        return baseColumns;
    };

    // 点击数字查看详情
    const handleDetailClick = async (type: string, record: SlaTableRow) => {
        const typeMap: { [key: string]: string } = {
            'actual_arrivals': t('wms.delivery.parcelSlaMonitoring.columns.actualArrivals'),
            'in_progress': t('wms.delivery.parcelSlaMonitoring.columns.inProgress'),
            'delivered_latest': t('wms.delivery.parcelSlaMonitoring.columns.deliveredLatest'),
            'failed_incomplete': t('wms.delivery.parcelSlaMonitoring.columns.failedNotReturned'),
            'returned_damaged_lost': t('wms.delivery.parcelSlaMonitoring.columns.returnedDamagedLost'),
            'picked': t('wms.delivery.parcelSlaMonitoring.columns.pickedCount'),
            'unpicked': t('wms.delivery.parcelSlaMonitoring.columns.unpickedCount'),
            'failed_returned_to_whs': t('wms.delivery.parcelSlaMonitoring.columns.failedReturnedToWhsCount'),
            'returned_parcel': t('wms.delivery.parcelSlaMonitoring.columns.returnedParcelCount'),
            'lost_damaged': t('wms.delivery.parcelSlaMonitoring.columns.lostDamaged'),
            'return_incomplete_72h': t('wms.delivery.parcelSlaMonitoring.detailDrawer.returnIncomplete72hDrillTitle'),
            'return_warehouse_24h_unprocessed': t('wms.delivery.parcelSlaMonitoring.detailDrawer.returnWarehouse24hDrillTitle')
        };

        // v2 未妥投归因：'v2:ALL'（未妥投总数）按状态「未妥投」筛；'v2:L1:L2'（子列）预填一级/二级原因
        const v2Decoded = type.startsWith('v2:') && type !== 'v2:ALL' ? decodeUndeliveredAnomaly(type) : null;
        // 标题按 ' - ' 切分推断类型，L1/L2 之间用不带空格的 '-'
        const v2Label = type === 'v2:ALL'
            ? t('wms.delivery.parcelSlaMonitoring.columns.undeliveredTotal')
            : (v2Decoded
                ? (v2Decoded.level2
                    ? `${getLevel1Label(t, v2Decoded.level1)}-${getLevel2LabelInGroup(t, v2Decoded.level2)}`
                    : getLevel1Label(t, v2Decoded.level1))
                : undefined);

        const isWhsRecord = 'warehouseName' in record;
        const displayName = isWhsRecord
            ? (record as WhsSlaData).warehouseName
            : (record as DspSlaData).dsp;
        setDrawerTitle(`${displayName} - ${v2Label ?? typeMap[type]} - ${t('wms.delivery.parcelSlaMonitoring.detailDrawer.detailPage')}`);

        const columns = getColumnsByType(type, handleDetailSortChange);
        setDrawerColumns(columns);

        let initialDsps: string[] = [];
        if (filters.selectedDsps.length > 0) {
            initialDsps = filters.selectedDsps;
        } else if (!isWhsRecord && (record as DspSlaData).dsp) {
            initialDsps = [(record as DspSlaData).dsp];
        }

        let statusFieldForContext: string | undefined;
        const anomalyStatusMap: { [key: string]: string } = {
            'unpicked24h': 'isUnpicked24h',
            'undelivered24h': 'isUndelivered24h',
            'undelivered48h': 'isUndelivered48h',
            'undelivered72h': 'isUndelivered72h',
            'aboutToTimeout24h': 'isAboutToTimeout24h',
            'aboutToTimeout48h': 'isAboutToTimeout48h',
            'lostDamaged': 'isLostDamaged',
            'noUpdate24h': 'isNoUpdate24h'
        };
        if (anomalyStatusMap[type]) {
            statusFieldForContext = anomalyStatusMap[type];
        } else {
            switch (type) {
                case 'actual_arrivals': statusFieldForContext = 'isActualArrivals'; break;
                case 'in_progress': case 'in_delivery': statusFieldForContext = 'isInDelivery'; break;
                case 'delivered_latest': statusFieldForContext = 'isDeliveredLatest'; break;
                case 'returned_damaged_lost': statusFieldForContext = 'isReturnedDamagedLost'; break;
                case 'failed_incomplete': statusFieldForContext = 'isFailedIncomplete'; break;
                case 'picked': statusFieldForContext = 'isPicked'; break;
                case 'unpicked': statusFieldForContext = 'isUnpicked'; break;
                case 'failed_returned_to_whs': statusFieldForContext = 'isFailedReturnedToWhs'; break;
                case 'returned_parcel': statusFieldForContext = 'isReturnedParcel'; break;
                case 'lost_damaged': statusFieldForContext = 'isLostDamaged'; break;
                case 'return_incomplete_72h': statusFieldForContext = 'isReturnedParcelIncompleteOver72h'; break;
                case 'return_warehouse_24h_unprocessed': statusFieldForContext = 'isFailedReturnedToWhsUnprocessedOver24h'; break;
                case 'v2:ALL': statusFieldForContext = 'isUndeliveredLc'; break;
                default: break;
            }
        }

        const initialStatuses = statusFieldForContext ? [statusFieldForContext] : [];
        const initialReasons = { undeliveredReasonLevel1: v2Decoded?.level1, undeliveredReasonLevel2: v2Decoded?.level2 };
        const initialDetailFilters = isWhsRecord
            ? { selectedDsps: [], selectedAreas: [], selectedDrivers: [], selectedStatuses: initialStatuses, selectedWarehouses: [(record as WhsSlaData).warehouseId].filter(Boolean).map(String), ...initialReasons }
            : { selectedDsps: initialDsps, selectedAreas: [], selectedDrivers: [], selectedStatuses: initialStatuses, selectedWarehouses: [], ...initialReasons };
        setDetailFilters(initialDetailFilters);
        setLastDetailContext({ type, record, statusField: statusFieldForContext });

        setDetailPage(1);
        setDetailPageSize(10);
        setDetailLoading(true);
        setDrawerData([]);
        setDetailTotal(0);
        setDrawerVisible(true);

        try {
            const { items, total } = await fetchDetailData(
                type,
                record,
                undefined,
                undefined,
                1,
                10,
                initialDetailFilters
            );
            setDrawerData(items);
            setDetailTotal(total);
        } catch (error) {
            console.error('获取详情数据失败:', error);
            setDrawerData([]);
            setDetailTotal(0);
        } finally {
            setDetailLoading(false);
        }
    };

    // 详情翻页（后端分页）
    const handleDetailPageChange = async (page: number, pageSize?: number) => {
        if (!lastDetailContext) return;
        
        setDetailLoading(true);
        // 如果 pageSize 未传入，使用当前 detailPageSize，但不应该小于10
        const size = pageSize ?? Math.max(detailPageSize, 10);
        setDetailPage(page);
        setDetailPageSize(size);
        const { type, record } = lastDetailContext;
        
        // 检查是否是异常预警类型（使用language-agnostic的type）
        const anomalyTypes = ['unpicked24h', 'undelivered24h', 'undelivered48h', 'undelivered72h', 'aboutToTimeout24h', 'aboutToTimeout48h', 'lostDamaged', 'noUpdate24h', 'noUpdate72h'];
        if (anomalyTypes.includes(type)) {
            // 优先使用抽屉内选择的状态筛选；清空时传空，后端返回所有状态数据
            const selectedStatuses = detailFilters.selectedStatuses;
            const statusField = selectedStatuses.length === 1 ? selectedStatuses[0] : undefined;
            const statusFields = selectedStatuses.length > 1 ? selectedStatuses : undefined;

            // 根据详情筛选条件构建查询参数，与dailyTask保持一致（不回退到主筛选条件）
            const selectedDspIdsFromDetailFilter = detailFilters.selectedDsps.length > 0
                ? detailFilters.selectedDsps.map(dspName => {
                    const dsp = dsps.find((d: { id: number; name: string }) => d.name === dspName);
                    return dsp ? dsp.id : null;
                }).filter(id => id !== null) as number[]
                : undefined;

            // 如果是DSP经理，自动添加DSP过滤条件
            const finalDspIds = selectedDspIdsFromDetailFilter;

            const warehouseIdsForDetail = resolveDetailWarehouseIds(
                detailFilters.selectedWarehouses.length > 0 ? detailFilters.selectedWarehouses : hubWarehouseIds,
                hubWarehouseIds,
                hubId ?? undefined
            );

            const selectedAreaIds = detailFilters.selectedAreas.length > 0
                ? detailFilters.selectedAreas.map(areaName => {
                    const area = areas.find((a: any) => a.name === areaName);
                    return area ? (typeof area.id === 'number' ? area.id : parseInt(String(area.id), 10)) : null;
                }).filter((id): id is number => id !== null)
                : undefined;
            const detailDriverUnassignedOnly = isUnassignedDriverOnly(detailFilters.selectedDrivers);
            const selectedDriverIds = detailDriverUnassignedOnly ? undefined : toDriverIds(detailFilters.selectedDrivers);

            const selectedCityNames = filters.selectedCities?.length ? filters.selectedCities : undefined;
            const hasDetailDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
            const detailStart = hasDetailDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
            const detailEnd = hasDetailDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
            const pageRequest: DeliveryProgressMonitoringOfSlaDetailPageRequestType = {
                filter: {
                    arrivalDateStart: detailStart,
                    arrivalDateEnd: detailEnd,
                    hubId: hubId ?? undefined,
                    warehouseIds: warehouseIdsForDetail,
                    dspIds: finalDspIds,
                    areaIds: selectedAreaIds,
                    driverIds: selectedDriverIds,
                    driverIdNullOnly: detailDriverUnassignedOnly ? true : undefined,
                    cityNames: selectedCityNames,
                    trackingNumber: undefined,
                    statusField,
                    statusFields,
                    arrivalPointType: (filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS),
                    undeliveredReasonLevel1: detailFilters.undeliveredReasonLevel1,
                    undeliveredReasonLevel2: detailFilters.undeliveredReasonLevel2,
                },
                page: page - 1,
                size: size,
                sortBy: detailSortConfig.sortBy,
                sortDirection: detailSortConfig.sortDirection
            };

            try {
                const { data: detailResponse } = await client.query({
                    query: GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
                    variables: { pageRequest }
                });

                const detailResp = detailResponse?.getDeliveryProgressMonitoringOfSlaDetailList;
                const detailItems = detailResp?.content || [];
                const totalElements = detailResp?.totalElements || 0;

                // 转换数据格式
                const convertedData: DetailData[] = detailItems.map((item: any) => ({
                    id: item.id,
                    dsp_name: (item.lastOperateDspName ?? item.lastAssignDspName) || '',
                    warehouse_name: item.warehouseName || '',
                    area_name: item.areaName || '',
                    driver_id: item.lastOperateDriverId ?? item.lastAssignDriverId ?? 0,
                    driver_name: (item.lastOperateDriverName ?? item.lastAssignDriverName) || '',
                    last_operate_dsp_name: item.lastOperateDspName || '',
                    last_operate_driver_name: item.lastOperateDriverName || '',
                    last_assign_dsp_name: item.lastAssignDspName || '',
                    last_assign_driver_name: item.lastAssignDriverName || '',
                    task_date: item.taskDate || '',
                    tracking_number: item.trackingNumber,
                    status: item.overallStatusText || item.overallStatus?.toString() || '',
                    undelivered_reason_level_1: item.undeliveredReasonLevel1 || undefined,
                    undelivered_reason_level_2: item.undeliveredReasonLevel2 || undefined,
                    order_id: item.orderId
                }));
                
                setDrawerData(convertedData);
                setDetailTotal(totalElements);
                // 确保分页大小与请求一致，使用我们请求的 size
                // 不依赖后端返回的 size，因为可能不正确
                setDetailPageSize(size);
            } catch (error) {
                console.error('获取异常详情数据失败:', error);
            }
        } else {
            // 普通详情分页处理
            const { items, total } = await fetchDetailData(type, record as WhsSlaData | DspSlaData, undefined, undefined, page, size);
            setDrawerData(items);
            setDetailTotal(total);
        }
        
        setDetailLoading(false);
    };

    // 点击异常预警查看详情（prefill 用于从二级 breakdown 页点击数字带入 DSP/仓库）
    const handleAnomalyClick = async (
        anomalyType: string,
        prefill?: { selectedDsps?: string[]; selectedWarehouses?: string[]; selectedDrivers?: string[] }
    ) => {
        // 重置详情分页状态到第一页，默认页面大小为10
        const initialPageSize = 10;
        setDetailPage(1);
        setDetailPageSize(initialPageSize);

        // v2 未妥投归因：'v2:ALL' 为"全部未妥投"，L1/L2 均不预填；'v2:L1[:L2]' 解码后作为 L1/L2 预填
        const v2Prefix = anomalyType.startsWith('v2:');
        const v2Decoded = v2Prefix && anomalyType !== 'v2:ALL' ? decodeUndeliveredAnomaly(anomalyType) : null;
        const undeliveredL1: UndeliveredLevel1 | undefined = v2Decoded?.level1;
        const undeliveredL2: UndeliveredLevel2 | undefined = v2Decoded?.level2;

        // 根据异常类型确定状态字段（用于默认状态筛选）
        const statusFieldForAnomaly = (() => {
            if (v2Prefix) return undefined;
            switch (anomalyType) {
                case 'actualArrivals': return 'isActualArrivals';
                case 'picked': return 'isPicked';
                case 'deliveredLatest': return 'isDeliveredLatest';
                case 'missScanWhsArrival': return 'isMissScanWhsArrival';
                case 'unpicked24h': return 'isUnpicked24h';
                case 'unpicked12h': return 'isUnpicked12h';
                case 'undelivered24h': return 'isUndelivered24h';
                case 'undelivered48h': return 'isUndelivered48h';
                case 'undelivered72h': return 'isUndelivered72h';
                case 'aboutToTimeout24h': return 'isAboutToTimeout24h';
                case 'aboutToTimeout48h': return 'isAboutToTimeout48h';
                case 'noUpdate24h': return 'isNoUpdate24h';
                case 'noUpdate72h': return 'isNoUpdate72h';
                case 'noUpdate48To72h': return 'isNoUpdate48To72h';
                case 'noUpdate48To72hWarehouse': return 'isNoUpdate48To72hWarehouse';
                case 'noUpdate48To72hDriver': return 'isNoUpdate48To72hDriver';
                case 'noUpdateOver72h': return 'isNoUpdateOver72h';
                case 'noUpdateOver72hWarehouse': return 'isNoUpdateOver72hWarehouse';
                case 'noUpdateOver72hDriver': return 'isNoUpdateOver72hDriver';
                case 'unpicked': return 'isUnpicked';
                case 'inDelivery': return 'isInDelivery';
                case 'failedNotReturned': return 'isFailedIncomplete';
                case 'failedReturnedToWhs': return 'isFailedReturnedToWhs';
                case 'returnedParcel': return 'isReturnedParcel';
                case 'lostDamaged': return 'isLostDamaged';
                // 汇总区分子下钻：退件72小时未完结 / 返仓24小时未处理
                case 'returnIncomplete72h': return 'isReturnedParcelIncompleteOver72h';
                case 'returnWarehouse24hUnprocessed': return 'isFailedReturnedToWhsUnprocessedOver24h';
                default: return undefined;
            }
        })();

        // 初始化详情筛选条件：有 prefill 时用 prefill（来自二级页，如断更司机侧点击行：带 dsp+司机）；否则主页未选 WHS 时详情 WHS 下拉显示为空，请求仍传全部 warehouseIds
        const initialDetailFilters = prefill
            ? {
                selectedDsps: prefill.selectedDsps ?? [],
                selectedAreas: [] as string[],
                selectedDrivers: prefill.selectedDrivers ?? [],
                selectedStatuses: statusFieldForAnomaly ? [statusFieldForAnomaly] : [],
                selectedWarehouses: prefill.selectedWarehouses ?? [],
                undeliveredReasonLevel1: undeliveredL1,
                undeliveredReasonLevel2: undeliveredL2,
            }
            : {
                selectedDsps: filters.selectedDsps.length > 0 ? filters.selectedDsps : [],
                selectedAreas: [] as string[],
                selectedDrivers: [] as string[],
                selectedStatuses: statusFieldForAnomaly ? [statusFieldForAnomaly] : [],
                selectedWarehouses: filters.selectedWarehouses.length > 0 ? filters.selectedWarehouses : [],
                undeliveredReasonLevel1: undeliveredL1,
                undeliveredReasonLevel2: undeliveredL2,
            };
        setDetailFilters(initialDetailFilters);

        // 根据type获取翻译后的label用于显示
        const anomalyTypeLabels: { [key: string]: string } = {
            'actualArrivals': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isActualArrivals'),
            'picked': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isPicked'),
            'deliveredLatest': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isDeliveredLatest'),
            'missScanWhsArrival': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isMissScanWhsArrival'),
            'unpicked24h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.notPickedUp24h'),
            'undelivered24h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.firstPickup24hNotDelivered'),
            'undelivered48h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.firstPickup48hNotDelivered'),
            'undelivered72h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.firstPickup72hNotDelivered'),
            'aboutToTimeout24h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.approachingTimeout24h'),
            'aboutToTimeout48h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.approachingTimeout48h'),
            'lostDamaged': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isLostDamaged'),
            'noUpdate24h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.noTracking24h'),
            'noUpdate72h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.noTracking72h'),
            'noUpdate48To72h': t('wms.delivery.parcelSlaMonitoring.break48to72h'),
            'noUpdate48To72hWarehouse': `${t('wms.delivery.parcelSlaMonitoring.break48to72h')}（${t('wms.delivery.parcelSlaMonitoring.warehouseSide')}）`,
            'noUpdate48To72hDriver': `${t('wms.delivery.parcelSlaMonitoring.break48to72h')}（${t('wms.delivery.parcelSlaMonitoring.driverSide')}）`,
            'noUpdateOver72h': t('wms.delivery.parcelSlaMonitoring.break72h'),
            'noUpdateOver72hWarehouse': `${t('wms.delivery.parcelSlaMonitoring.break72h')}（${t('wms.delivery.parcelSlaMonitoring.warehouseSide')}）`,
            'noUpdateOver72hDriver': `${t('wms.delivery.parcelSlaMonitoring.break72h')}（${t('wms.delivery.parcelSlaMonitoring.driverSide')}）`,
            'unpicked': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isUnpicked'),
            'inDelivery': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isInDelivery'),
            'failedNotReturned': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isFailedIncomplete'),
            'failedReturnedToWhs': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isFailedReturnedToWhs'),
            'returnedParcel': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isReturnedParcel')
            ,
            // 汇总区分子下钻
            'returnIncomplete72h': t('wms.delivery.parcelSlaMonitoring.detailDrawer.returnIncomplete72hDrillTitle'),
            'returnWarehouse24hUnprocessed': t('wms.delivery.parcelSlaMonitoring.detailDrawer.returnWarehouse24hDrillTitle')
        };
        const v2Label = v2Prefix
            ? (anomalyType === 'v2:ALL'
                ? t('wms.delivery.parcelSlaMonitoring.detailDrawer.allUndeliveredDrillTitle', { defaultValue: '全部未妥投' })
                : (v2Decoded
                    ? (v2Decoded.level2
                        ? `${getLevel1Label(t, v2Decoded.level1)} - ${getLevel2Label(t, v2Decoded.level2)}`
                        : getLevel1Label(t, v2Decoded.level1))
                    : null))
            : null;
        const anomalyLabel = v2Label ?? anomalyTypeLabels[anomalyType] ?? anomalyType;

        // 根据筛选条件设置抽屉标题（已移除城市筛选条件）
        if (filters.selectedDsps.length > 1 || filters.selectedWarehouses.length > 1) {
            setDrawerTitle(`${anomalyLabel} - ${t('wms.delivery.parcelSlaMonitoring.detailDrawer.detailPage')} (${t('wms.delivery.parcelSlaMonitoring.detailDrawer.multipleFilters')})`);
        } else {
            setDrawerTitle(`${anomalyLabel} - ${t('wms.delivery.parcelSlaMonitoring.detailDrawer.detailPage')}`);
        }
        
        // 使用统一的列定义函数，支持排序
        const columns = getColumnsByType(anomalyLabel, handleDetailSortChange);
        setDrawerColumns(columns);
        
        const statusField = statusFieldForAnomaly;

        // 如果汇总为 0，则直接显示空结果，避免"汇总为0但详情有数据"的错觉
        // 注意：anomalySummary 已移除，不再进行汇总检查
        if (false) {
            const countMap: Record<string, number> = {};
            if ((countMap[anomalyType] || 0) === 0) {
                setDrawerColumns(columns);
                setDrawerData([]);
                setDrawerVisible(true);
                return;
            }
        }

        // 根据详情筛选条件构建查询参数，使用刚刚设置的 initialDetailFilters 而不是旧的 detailFilters
        const selectedDspIdsFromDetailFilter = initialDetailFilters.selectedDsps.length > 0
            ? initialDetailFilters.selectedDsps.map(dspName => {
                const dsp = dsps.find((d: { id: number; name: string }) => d.name === dspName);
                return dsp ? dsp.id : null;
            }).filter(id => id !== null) as number[]
            : undefined;

        const finalDspIds = selectedDspIdsFromDetailFilter;

        // 汇总数据点击与饼图点击均走此处：未选 WHS 时传全部 warehouseIds，避免 []/[0] 查不到值
        const warehouseIdsForAnomaly = resolveDetailWarehouseIds(
            initialDetailFilters.selectedWarehouses.length > 0 ? initialDetailFilters.selectedWarehouses : hubWarehouseIds,
            hubWarehouseIds,
            hubId ?? undefined
        );

        // 名称→id 走累积缓存：initialDetailFilters 是本帧刚构造的，选项数组还没并入这些已选项
        const selectedAreaIds = initialDetailFilters.selectedAreas.length > 0
            ? initialDetailFilters.selectedAreas
                .map(areaName => areaIdByName.current.get(areaName) ?? null)
                .filter((id): id is number => id !== null)
            : undefined;
        const initialDriverUnassignedOnly = isUnassignedDriverOnly(initialDetailFilters.selectedDrivers);
        const selectedDriverIds = initialDriverUnassignedOnly ? undefined : toDriverIds(initialDetailFilters.selectedDrivers);

        const selectedCityNames = filters.selectedCities?.length ? filters.selectedCities : undefined;
        const hasInitialDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
        const initialStart = hasInitialDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const initialEnd = hasInitialDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const pageRequest: DeliveryProgressMonitoringOfSlaDetailPageRequestType = {
            filter: {
                arrivalDateStart: initialStart,
                arrivalDateEnd: initialEnd,
                hubId: hubId ?? undefined,
                warehouseIds: warehouseIdsForAnomaly,
                dspIds: finalDspIds,
                areaIds: selectedAreaIds,
                driverIds: selectedDriverIds,
                driverIdNullOnly: initialDriverUnassignedOnly ? true : undefined,
                cityNames: selectedCityNames,
                trackingNumber: undefined,
                statusField,
                statusFields: undefined,
                arrivalPointType: (filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS),
                undeliveredReasonLevel1: undeliveredL1,
                undeliveredReasonLevel2: undeliveredL2,
            },
            page: 0,
            size: initialPageSize,
            sortBy: detailSortConfig.sortBy,
            sortDirection: detailSortConfig.sortDirection
        };

        const virtualRecord: WhsSlaData = {
            id: 'anomaly',
            warehouseId: 0,
            warehouseName: '异常预警',
                actualArrivals: 0,
                pickedCount: 0,
                unpickedCount: 0,
                inProgress: 0,
                deliveredLatest: 0,
                failedIncomplete: 0,
                latestDeliveryRate: 0,
                failedReturnedToWhsCount: 0,
                returnedParcelCount: 0,
                lostDamagedCount: 0,
                returnedParcelIncompleteOver72hRate: 0,
                failedReturnedToWhsUnprocessedOver24hRate: 0,
                t1DeliveryRate: 0,
                t2DeliveryRate: 0,
                t3DeliveryRate: 0,
                t4DeliveryRate: 0,
                t5DeliveryRate: 0
        };
        setLastDetailContext({ type: anomalyType, record: virtualRecord, statusField });
        setDetailLoading(true);
        setDrawerData([]);
        setDetailTotal(0);
        setDrawerVisible(true);

        try {
            // 直接调用详情查询API
            const { data: detailResponse } = await client.query({
                query: GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
                variables: { pageRequest }
            });

            const detailResp = detailResponse?.getDeliveryProgressMonitoringOfSlaDetailList;
            const detailItems = detailResp?.content || [];
            const totalElements = detailResp?.totalElements || 0;

                // 转换数据格式（必须包含 id 作为 rowKey，避免同 tracking_number 时重复渲染）
            const convertedData: DetailData[] = detailItems.map((item: any) => ({
                id: item.id,
                dsp_name: (item.lastOperateDspName ?? item.lastAssignDspName) || '',
                warehouse_name: item.warehouseName || '',
                area_name: item.areaName || '',
                driver_id: item.lastOperateDriverId ?? item.lastAssignDriverId ?? 0,
                driver_name: (item.lastOperateDriverName ?? item.lastAssignDriverName) || '',
                last_operate_dsp_name: item.lastOperateDspName || '',
                last_operate_driver_name: item.lastOperateDriverName || '',
                last_assign_dsp_name: item.lastAssignDspName || '',
                last_assign_driver_name: item.lastAssignDriverName || '',
                task_date: item.taskDate || '',
                tracking_number: item.trackingNumber,
                status: item.overallStatusText || item.overallStatus?.toString() || '',
                undelivered_reason_level_1: item.undeliveredReasonLevel1 || undefined,
                undelivered_reason_level_2: item.undeliveredReasonLevel2 || undefined,
                order_id: item.orderId
            }));
            
            setDrawerData(convertedData);
            setDetailTotal(totalElements); // 设置正确的总数
            // 确保分页大小与请求一致，使用我们设置的 initialPageSize（10）
            // 不依赖后端返回的 size，因为可能不正确
            setDetailPageSize(initialPageSize);
            // 重新设置列定义，确保排序功能正常
            setDrawerColumns(getColumnsByType(anomalyType, handleDetailSortChange));
        } catch (error) {
            console.error('获取异常详情数据失败:', error);
            setDrawerData([]);
            setDetailTotal(0);
        } finally {
            setDetailLoading(false);
        }
    };

    // 请求断更司机侧 breakdown 某一页数据（共用变量构建）
    const fetchBreakdownPage = async (anomalyType: string, page: number, pageSizeOverride?: number) => {
        const size = pageSizeOverride ?? breakdownPageSize;
        const selectedDspIds = filters.selectedDsps.length > 0
            ? filters.selectedDsps.map(dspName => {
                const dsp = dsps.find((d: { id: number; name: string }) => d.name === dspName);
                return dsp ? dsp.id : null;
            }).filter(id => id !== null) as number[]
            : undefined;
        const finalDspIds = selectedDspIds;
        const warehouseIdsForBreakdown = toWarehouseIdsNum(
            filters.selectedWarehouses.length > 0 ? filters.selectedWarehouses : hubWarehouseIds
        );
        const arrivalPointType = (filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS);
        const hasDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
        const arrivalDateStart = hasDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const arrivalDateEnd = hasDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const breakType: NoUpdateBreakType = anomalyType === 'noUpdate48To72h' ? 'NO_UPDATE_48_TO_72H' : 'NO_UPDATE_OVER_72H';
        const { data } = await client.query({
            query: GET_BREAK_DRIVER_SIDE_BREAKDOWN,
            variables: {
                breakType,
                filter: {
                    arrivalDateStart,
                    arrivalDateEnd,
                    warehouseIds: warehouseIdsForBreakdown,
                    dspIds: finalDspIds,
                    areaIds: undefined,
                    driverIds: undefined,
                    cityNames: filters.selectedCities?.length ? filters.selectedCities : undefined,
                    arrivalPointType,
                    dimension: filters.viewMode,
                },
                page,
                size,
            }
        });
        const res = data?.getBreakDriverSideBreakdown;
        if (res) {
            setBreakdownData(res.content ?? []);
            setBreakdownPage(res.page ?? 1);
            setBreakdownTotal(res.totalElements ?? 0);
            setBreakdownTotalPages(res.totalPages ?? 0);
            if (pageSizeOverride !== undefined) {
                setBreakdownPageSize(pageSizeOverride);
            }
        }
    };

    // 断更「司机侧」点击：先打开二级页（DSP/司机数量 breakdown），再在二级页点击数字打开明细抽屉
    const handleDriverSideBreakdownClick = async (anomalyType: string) => {
        setBreakdownAnomalyType(anomalyType);
        setBreakdownTitle(
            String(
                anomalyType === 'noUpdate48To72h'
                    ? t('wms.delivery.parcelSlaMonitoring.breakdown.aboutToBreakTitle')
                    : t('wms.delivery.parcelSlaMonitoring.breakdown.alreadyBreakTitle')
            )
        );
        setBreakdownData([]);
        setBreakdownDrawerVisible(true);
        setBreakdownLoading(true);
        try {
            await fetchBreakdownPage(anomalyType, 1);
            setBreakdownDrawerVisible(true);
        } catch (error) {
            console.error('获取断更司机侧 breakdown 失败:', error);
            setBreakdownData([]);
            setBreakdownDrawerVisible(true);
        } finally {
            setBreakdownLoading(false);
        }
    };

    const handleBreakdownPageChange = (page: number) => {
        setBreakdownLoading(true);
        fetchBreakdownPage(breakdownAnomalyType, page)
            .catch((error) => console.error('获取断更司机侧 breakdown 分页失败:', error))
            .finally(() => setBreakdownLoading(false));
    };

    const handleBreakdownPageSizeChange = (size: number) => {
        setBreakdownLoading(true);
        fetchBreakdownPage(breakdownAnomalyType, 1, size)
            .catch((error) => console.error('获取断更司机侧 breakdown 分页失败:', error))
            .finally(() => setBreakdownLoading(false));
    };

    const handleBreakdownRowClick = (anomalyType: string, dspId: number, dspName: string, driverId?: number, driverName?: string, driverIds?: number[]) => {
        // 二级页的司机名称不一定在搜索选项里，先记下行上的 id 供下游反查
        if (driverName && driverId) driverNameById.current.set(Number(driverId), driverName);

        // 不关闭二级抽屉，仅打开明细抽屉，便于关掉包裹详情后仍能看到按 DSP 分组的二级页
        setDetailDrawerDimensionOverride('dsp');
        const selectedDrivers = (driverIds?.length ? driverIds : (driverId ? [driverId] : [])).map(String);
        // 司机侧二级页点进详情时使用司机侧状态字段
        const detailAnomalyType = anomalyType === 'noUpdate48To72h' ? 'noUpdate48To72hDriver' : anomalyType === 'noUpdateOver72h' ? 'noUpdateOver72hDriver' : anomalyType;
        handleAnomalyClick(detailAnomalyType, {
            selectedDsps: [dspName],
            selectedWarehouses: [],
            selectedDrivers
        });
    };

    // 监听排序状态变化，重新生成列定义
    useEffect(() => {
        if (drawerVisible && drawerTitle) {
            const titleParts = drawerTitle.split(' - ');
            if (titleParts.length >= 2) {
                const type = titleParts[0]; // 类型在第一个部分
                const newColumns = getColumnsByType(type, handleDetailSortChange);
                setDrawerColumns(newColumns);
            }
        }
    }, [detailSortConfig.sortBy, detailSortConfig.sortDirection, drawerVisible, drawerTitle]);

    // 监听详情筛选条件变化（含状态清空），重新查询详情数据
    const detailFiltersStr = JSON.stringify(detailFilters);
    useEffect(() => {
        if (!drawerVisible || !lastDetailContext) return;
        const { type, record } = lastDetailContext;
        setDetailPage(1);
        setDetailLoading(true);
        fetchDetailData(type, record as WhsSlaData | DspSlaData, detailSortConfig.sortBy, detailSortConfig.sortDirection, 1, detailPageSize)
            .then(({ items, total }) => {
                setDrawerData(items);
                setDetailTotal(total);
            })
            .catch((error) => {
                console.error('详情筛选条件变化后重新获取数据失败:', error);
            })
            .finally(() => {
                setDetailLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detailFiltersStr, drawerVisible, lastDetailContext?.type]);

    // 稳定化预设值字符串，确保预设变化时能触发查询
    const arrivalTimeDatePresetStr = useMemo(() => {
        return filters.arrivalTimeDatePreset ?? null;
    }, [filters.arrivalTimeDatePreset]);

    // 初始化查询和分页查询（与 getDeliveryProgressMonitoringOfSlaPageList 一致：filter 用 listFilter，仅加分页）
    // 未选 WHS 时等仓库列表加载完再请求，保证与「清空 WHS」传参一致（都传完整列表或都传 undefined），避免查出结果不一致
    useEffect(() => {
        if (hubId == null) return;
        if (filters.selectedWarehouses.length === 0 && hubWarehouseIds.length === 0 && warehousesLoading) return;
        const pageRequest: DeliveryProgressMonitoringOfSlaPageRequestType = {
            filter: listFilter,
            pageRequest: {
                page: currentPage,
                size: pageSize,
                sortBy: sortConfig.sortBy,
                sortDirection: sortConfig.sortDirection,
            }
        };
        handleFilter(pageRequest);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        hubId,
        listFilter,
        currentPage,
        pageSize,
        sortConfig.sortBy,
        sortConfig.sortDirection,
        warehousesLoading,
    ]);

    // 分页处理函数
    const handlePageChange = (page: number, newPageSize?: number) => {
        setCurrentPage(page);
        if (newPageSize && newPageSize !== pageSize) {
            setPageSize(newPageSize);
        }
    };

    // 处理排序变化
    const handleSortChange = (sortBy: string, sortDirection: 'ASC' | 'DESC') => {
        // 更新排序状态
        setSortConfig({ sortBy, sortDirection });
        // 重置到第一页
        setCurrentPage(1);
    };

    // 处理详情排序变化
    const handleDetailSortChange = async (sortBy: string, sortDirection: 'ASC' | 'DESC') => {
        // 更新排序状态
        setDetailSortConfig({ sortBy, sortDirection });
        
        // 如果抽屉是打开的，重新获取数据
        if (drawerVisible && drawerData.length > 0) {
            // 获取当前详情类型（从抽屉标题推断）
            const titleParts = drawerTitle.split(' - ');
            
            if (titleParts.length >= 2) {
                // 异常预警标题格式: "超24小时未领取 - 详情页"
                // 正常详情标题格式: "区域A - 实际到件 - 详情页"
                let type: string;
                if (titleParts.length === 2) {
                    // 异常预警格式: "超24小时未领取 - 详情页"
                    type = titleParts[0];
                } else if (titleParts.length === 3) {
                    // 正常详情格式: "区域A - 实际到件 - 详情页"
                    type = titleParts[1];
                } else {
                    type = titleParts[0];
                }
                
                // 检查是否是异常预警
                if (ANOMALY_TYPE_MAP[type]) {
                    // 重新调用异常预警查询
                    try {
                        // 优先使用详情筛选条件，与dailyTask保持一致
                        const selectedDspIdsFromDetailFilter = detailFilters.selectedDsps.length > 0
                            ? detailFilters.selectedDsps.map(dspName => {
                                const dsp = dsps.find((d: { id: number; name: string }) => d.name === dspName);
                                return dsp ? dsp.id : null;
                            }).filter(id => id !== null) as number[]
                            : undefined;
                        
                        const selectedDspIdsFromFilter = filters.selectedDsps.length > 0 
                            ? filters.selectedDsps.map(dspName => {
                                const dsp = dsps.find((d: { id: number; name: string }) => d.name === dspName);
                                return dsp ? dsp.id : null;
                            }).filter(id => id !== null) as number[]
                            : undefined;

                        // 如果是DSP经理，自动添加DSP过滤条件
                        const selectedDspIds = selectedDspIdsFromDetailFilter || selectedDspIdsFromFilter;

                        const warehouseIdsForSortDetail = resolveDetailWarehouseIds(
                            detailFilters.selectedWarehouses.length > 0 ? detailFilters.selectedWarehouses : hubWarehouseIds,
                            hubWarehouseIds,
                            hubId ?? undefined
                        );

                        const selectedCityNames = filters.selectedCities?.length ? filters.selectedCities : undefined;

                        // 清空状态时传空，后端返回所有状态数据
                        const selectedStatuses = detailFilters.selectedStatuses;
                        const statusField = selectedStatuses.length === 1 ? selectedStatuses[0] : undefined;
                        const statusFields = selectedStatuses.length > 1 ? selectedStatuses : undefined;
                        const hasBreakdownDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
                        const breakdownStart = hasBreakdownDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
                        const breakdownEnd = hasBreakdownDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
                        const pageRequest: DeliveryProgressMonitoringOfSlaDetailPageRequestType = {
                            filter: {
                                arrivalDateStart: breakdownStart,
                                arrivalDateEnd: breakdownEnd,
                                hubId: hubId ?? undefined,
                                warehouseIds: warehouseIdsForSortDetail,
                                dspIds: selectedDspIds,
                                areaIds: undefined,
                                driverIds: undefined,
                                cityNames: selectedCityNames,
                                trackingNumber: undefined,
                                statusField,
                                statusFields,
                                arrivalPointType: (filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS),
                                undeliveredReasonLevel1: detailFilters.undeliveredReasonLevel1,
                                undeliveredReasonLevel2: detailFilters.undeliveredReasonLevel2,
                            },
                            page: detailPage - 1,
                            size: detailPageSize,
                            sortBy: sortBy,
                            sortDirection: sortDirection
                        };

                        const { data: detailResponse } = await client.query({
                            query: GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
                            variables: { pageRequest }
                        });

                        const detailResp = detailResponse?.getDeliveryProgressMonitoringOfSlaDetailList;
                        const detailItems = detailResp?.content || [];
                        const convertedData: DetailData[] = detailItems.map((item: any) => ({
                            id: item.id,
                            dsp_name: (item.lastOperateDspName ?? item.lastAssignDspName) || '',
                            warehouse_name: item.warehouseName || '',
                            area_name: item.areaName || '',
                            driver_id: item.lastOperateDriverId ?? item.lastAssignDriverId ?? 0,
                            driver_name: (item.lastOperateDriverName ?? item.lastAssignDriverName) || '-',
                            last_operate_dsp_name: item.lastOperateDspName || '',
                            last_operate_driver_name: item.lastOperateDriverName || '',
                            last_assign_dsp_name: item.lastAssignDspName || '',
                            last_assign_driver_name: item.lastAssignDriverName || '',
                            task_date: item.taskDate || '',
                            tracking_number: item.trackingNumber,
                            status: item.overallStatusText || item.overallStatus?.toString() || '',
                            undelivered_reason_level_1: item.undeliveredReasonLevel1 || undefined,
                            undelivered_reason_level_2: item.undeliveredReasonLevel2 || undefined,
                            order_id: item.orderId
                        }));
                        
                        setDrawerData(convertedData);
                    } catch (error) {
                        console.error('重新获取异常详情数据失败:', error);
                    }
                } else if (NORMAL_TYPE_MAP[type]) {
                    // 重新获取普通详情数据
                    const detailType = NORMAL_TYPE_MAP[type];
                    try {
                        // 使用保存的原始详情上下文数据
                        const defaultRecord: WhsSlaData = {
                            id: '',
                            warehouseId: 0,
                            warehouseName: titleParts[0] || '',
                            actualArrivals: 0,
                            pickedCount: 0,
                            unpickedCount: 0,
                            inProgress: 0,
                            deliveredLatest: 0,
                            failedIncomplete: 0,
                            latestDeliveryRate: 0,
                            failedReturnedToWhsCount: 0,
                            returnedParcelCount: 0,
                            lostDamagedCount: 0,
                            returnedParcelIncompleteOver72hRate: 0,
                            failedReturnedToWhsUnprocessedOver24hRate: 0,
                            t1DeliveryRate: 0,
                            t2DeliveryRate: 0,
                            t3DeliveryRate: 0,
                            t4DeliveryRate: 0,
                            t5DeliveryRate: 0
                        };
                        const originalRecord: SlaTableRow = lastDetailContext?.record ?? defaultRecord;

                        const { items } = await fetchDetailData(detailType, originalRecord as WhsSlaData | DspSlaData, sortBy, sortDirection);
                        setDrawerData(items);
                    } catch (error) {
                        console.error('重新获取详情数据失败:', error);
                    }
                }
            }
        }
    };

    // 处理过滤器更新
    const handleFiltersUpdate = (newFilters: SlaMonitoringFilters) => {
        setCurrentPage(1);
        setFilters(newFilters);
    };

    // 切换 WHS/DSP 维度：只改维度与排序，保留 WHS/DSP 下拉筛选
    const handleViewModeChange = (mode: SlaMonitoringViewMode) => {
        setSortConfig({
            sortBy: 'overallConversionRate',
            sortDirection: 'DESC',
        });
        setFilters((prev) => ({
            ...prev,
            viewMode: mode,
        }));
        setCurrentPage(1);
    };

    // 区域/司机共用同一接口，分页、loading、汇总、记录均来自单次查询
    const currentPagination = apiPagination;
    const currentLoading = loading;
    const currentSummaryData = summaryData;

    // 数据更新时间（用于页面展示：数据更新于 XXX）——使用当前维度数据的 calculatedAt，统一转为浏览器本地时间
    const [dataUpdatedAtText, setDataUpdatedAtText] = useState<string>('');
    useEffect(() => {
        const calculatedAt = recordData.length > 0 ? recordData[0]?.calculatedAt : null;
        if (!calculatedAt) {
            setDataUpdatedAtText('');
            return;
        }
        try {
            const d = dayjs.utc(calculatedAt);
            setDataUpdatedAtText(d.isValid() ? d.local().format('YYYY-MM-DD HH:mm:ss') : String(calculatedAt));
        } catch {
            setDataUpdatedAtText(String(calculatedAt));
        }
    }, [recordData]);

    return {
        filters,
        setFilters: handleFiltersUpdate,
        whsData,
        dspData,
        viewMode: filters.viewMode,
        handleViewModeChange,
        anomalyData,
        summaryData: currentSummaryData,
        dsps,
        warehouses: structuralWarehouses,
        drawerVisible,
        drawerTitle,
        drawerData,
        drawerColumns,
        setDrawerVisible,
        handleDetailClick,
        handleAnomalyClick,
        breakdownDrawerVisible,
        setBreakdownDrawerVisible,
        breakdownData,
        breakdownTitle,
        breakdownAnomalyType,
        breakdownLoading,
        breakdownPage,
        breakdownPageSize,
        breakdownTotal,
        breakdownTotalPages,
        handleDriverSideBreakdownClick,
        handleBreakdownPageChange,
        handleBreakdownPageSizeChange,
        handleBreakdownRowClick,
        detailDrawerDimensionOverride,
        detailFilters,
        setDetailFilters,
        areas,
        drivers,
        detailDrawerDsps,
        detailDrawerAreas,
        detailDrawerDrivers,
        drawerDrivers,
        loading: currentLoading,
        error,
        pagination: currentPagination ? {
            current: currentPagination.page,
            pageSize: currentPagination.size,
            total: currentPagination.totalElements,
        } : null,
        tablePagination: currentPagination ? {
            page: currentPagination.page,
            size: currentPagination.size,
            totalElements: currentPagination.totalElements,
            totalPages: currentPagination.totalPages,
            hasNext: currentPagination.hasNext,
            hasPrevious: currentPagination.hasPrevious,
        } : undefined,
        handlePageChange,
        handleSortChange,
        handleDetailSortChange,
        // 详情分页与翻页
        detailPagination: { page: detailPage, size: detailPageSize, total: detailTotal },
        handleDetailPageChange,
        detailSortConfig,
        detailLoading,
        orderDetailVisible,
        orderId,
        setOrderDetailVisible,
        showOrderDetail,
        warehouseId: filters.selectedWarehouses[0] ?? hubWarehouseIds[0] ?? (hubId != null ? String(hubId) : ''),
        lastDetailContext,
        dataUpdatedAtText,
        sortConfig,
    };
};
